import { Component, createSignal, For, Show, onCleanup } from "solid-js";
import { Kbd } from "../ui/Kbd";
import type { WorkItemStatus } from "../../api";

export interface BulkActionBarProps {
  selectedCount: number;
  statuses: WorkItemStatus[];
  onChangeStatus: (statusId: string) => void;
  onDeselectAll: () => void;
}

/** Group label for status categories */
const categoryLabel: Record<string, string> = {
  todo: "TO DO",
  in_progress: "IN PROGRESS",
  done: "DONE",
  cancelled: "CANCELLED",
};

/** Dot color for status categories */
const categoryDotColor: Record<string, string> = {
  todo: "bg-line-strong",
  in_progress: "bg-signal-blue",
  done: "bg-signal-green",
  cancelled: "bg-signal-red",
};

/**
 * BulkActionBar - Floating ink bar at the bottom of the viewport.
 * Appears when backlog items are selected, offering bulk status change.
 */
export const BulkActionBar: Component<BulkActionBarProps> = (props) => {
  const [open, setOpen] = createSignal(false);

  const handleDocClick = (e: MouseEvent) => {
    if (!(e.target as HTMLElement).closest("[data-bulk-dropdown]")) {
      setOpen(false);
    }
  };

  if (typeof document !== "undefined") {
    document.addEventListener("click", handleDocClick, true);
    onCleanup(() =>
      document.removeEventListener("click", handleDocClick, true),
    );
  }

  const groupedStatuses = () => {
    const groups: { category: string; statuses: WorkItemStatus[] }[] = [];
    const categoryOrder = ["todo", "in_progress", "done", "cancelled"];
    const byCategory = new Map<string, WorkItemStatus[]>();

    for (const s of props.statuses) {
      const list = byCategory.get(s.category) ?? [];
      list.push(s);
      byCategory.set(s.category, list);
    }

    for (const cat of categoryOrder) {
      const list = byCategory.get(cat);
      if (list && list.length > 0) {
        groups.push({
          category: cat,
          statuses: list.sort((a, b) => a.position - b.position),
        });
      }
    }
    return groups;
  };

  const handleStatusClick = (statusId: string) => {
    setOpen(false);
    props.onChangeStatus(statusId);
  };

  return (
    <div class="fixed bottom-16 left-1/2 -translate-x-1/2 z-40 animate-in slide-in-from-bottom-2 duration-200">
      <div
        data-bulk-bar
        class="flex items-center h-11 px-4 bg-ink text-paper border border-ink-2 rounded-md shadow-overlay gap-3"
      >
        {/* Selection count */}
        <div class="flex items-center gap-1.5">
          <svg
            class="w-4 h-4 text-signal-green"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          <span class="text-sm font-medium text-paper whitespace-nowrap">
            {props.selectedCount} selected
          </span>
        </div>

        {/* Divider */}
        <div class="w-px h-5 bg-ink-2" />

        {/* Status dropdown */}
        <div class="relative" data-bulk-dropdown>
          <button
            type="button"
            class="flex items-center gap-1.5 text-sm font-medium text-paper bg-ink hover:bg-ink-2 border border-ink-2 rounded px-3 py-1.5 transition-colors cursor-pointer focus-ring"
            onClick={() => setOpen(!open())}
          >
            Move to
            <svg
              class={
                "w-3.5 h-3.5 text-paper/60 transition-transform duration-150 " +
                (open() ? "rotate-180" : "")
              }
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown menu (opens upward) */}
          <Show when={open()}>
            <div class="absolute bottom-full mb-2 left-0 w-48 bg-ink border border-ink-2 rounded shadow-overlay overflow-hidden">
              <div class="max-h-64 overflow-y-auto py-1">
                <For each={groupedStatuses()}>
                  {(group) => (
                    <>
                      <div class="px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-paper/60">
                        {categoryLabel[group.category] ?? group.category}
                      </div>
                      <For each={group.statuses}>
                        {(status) => (
                          <button
                            type="button"
                            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-paper hover:bg-ink-2 transition-colors cursor-pointer"
                            onClick={() => handleStatusClick(status.id)}
                          >
                            <span
                              class={
                                "w-2.5 h-2.5 rounded-full shrink-0 " +
                                (categoryDotColor[status.category] ??
                                  "bg-line-strong")
                              }
                            />
                            {status.name}
                          </button>
                        )}
                      </For>
                    </>
                  )}
                </For>
              </div>
            </div>
          </Show>
        </div>

        {/* Divider */}
        <div class="w-px h-5 bg-ink-2" />

        {/* Deselect all */}
        <button
          type="button"
          class="flex items-center gap-1.5 text-sm text-paper/80 hover:text-paper transition-colors cursor-pointer"
          onClick={props.onDeselectAll}
        >
          Deselect
          <Kbd variant="inverse">Esc</Kbd>
        </button>
      </div>
    </div>
  );
};
